import type { MovieDetail } from '@zyx/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ErrorState } from '@/components/common/states';
import { formatBytes } from '@/lib/crawl-labels';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useApi } from '@/hooks/use-connection';
import { useRenditions, useStartDownload } from '@/hooks/use-downloads';
import { editionLabel, formatAgo, qualityLabel } from '@/lib/format';
import { queryKeys } from '@/lib/query-keys';

/** The editions a dialog can act on: the ones the source still has. */
function liveEditions(movie: MovieDetail) {
  return movie.variants.filter((variant) => variant.gone !== true);
}

/** Fetches some or all of the movie's pages again now, and waits for that job to finish. */
function RefreshForm(props: { readonly movie: MovieDetail; readonly onDone: () => void }) {
  const api = useApi();
  const client = useQueryClient();
  const editions = liveEditions(props.movie);
  const [chosen, setChosen] = useState<ReadonlySet<number>>(() => new Set(editions.map((variant) => variant.variantId)));
  const [checkStreams, setCheckStreams] = useState(false);
  const start = useMutation({
    mutationFn: () => api.refreshMovie({ code: props.movie.code, variantIds: [...chosen], checkStreams }),
    onError: (error) => toast.error(error instanceof Error ? error.message : 'The refresh did not start.'),
  });
  const jobId = start.data?.job.jobId;
  const job = useQuery({
    queryKey: ['jobs', 'detail', jobId],
    queryFn: () => api.getJob(jobId ?? 0),
    enabled: jobId !== undefined,
    refetchInterval: (query) => (query.state.data === undefined || ['running', 'pending'].includes(query.state.data.job.status) ? 1_000 : false),
  });
  const status = job.data?.job.status;
  const finished = status === 'completed' || status === 'failed' || status === 'cancelled';

  const clean = status === 'completed' && (job.data?.job.progress.dead ?? 0) === 0;
  const { code } = props.movie;
  const { onDone } = props;
  // Once, when the job finishes: the movie reloads, and a clean finish closes the dialog.
  const handled = useRef(false);
  useEffect(() => {
    if (!finished || handled.current) {
      return;
    }
    handled.current = true;
    void client.invalidateQueries({ queryKey: queryKeys.movies() });
    if (clean) {
      toast.success(`${code} is up to date.`);
      onDone();
    }
  }, [finished, clean, client, code, onDone]);

  const progress = job.data?.job.progress;
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        start.mutate();
      }}
    >
      <DialogHeader>
        <DialogTitle>Refresh {props.movie.code}</DialogTitle>
        <DialogDescription>Fetch its pages from the source again now, updating the title, cast, genres, counts and episodes.</DialogDescription>
      </DialogHeader>
      {editions.length === 0 ? (
        <p className="text-sm text-muted-foreground">The source no longer has any page of this movie.</p>
      ) : (
        <ul className="space-y-2">
          {editions.map((variant) => (
            <li key={variant.variantId} className="flex items-center gap-2">
              <Checkbox
                id={`refresh-${variant.variantId}`}
                checked={chosen.has(variant.variantId)}
                disabled={jobId !== undefined}
                onCheckedChange={(checked) => {
                  const next = new Set(chosen);
                  if (checked === true) {
                    next.add(variant.variantId);
                  } else {
                    next.delete(variant.variantId);
                  }
                  setChosen(next);
                }}
              />
              <Label htmlFor={`refresh-${variant.variantId}`} className="font-normal">
                {editionLabel(variant.edition)} on {variant.sourceId}
                <span className="text-muted-foreground"> · fetched {formatAgo(variant.indexedAt) ?? 'never'}</span>
              </Label>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <Checkbox id="refresh-streams" checked={checkStreams} disabled={jobId !== undefined} onCheckedChange={(checked) => setCheckStreams(checked === true)} />
        <Label htmlFor="refresh-streams" className="font-normal">
          Also check that its video streams still play
        </Label>
      </div>
      {jobId === undefined ? null : (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          {finished ? null : <Loader2 className="size-4 animate-spin" />}
          {status === undefined || !finished
            ? `Refreshing… ${progress === undefined ? '' : `${progress.done} of ${progress.done + progress.pending + progress.claimed + progress.failed} done`}`
            : status === 'completed' && (progress?.dead ?? 0) === 0
              ? 'Done.'
              : `Finished with problems: see job ${jobId} under Indexing.`}
        </p>
      )}
      <DialogFooter>
        <Button type="submit" disabled={chosen.size === 0 || start.isPending || jobId !== undefined}>
          <RefreshCw />
          Refresh
        </Button>
      </DialogFooter>
    </form>
  );
}

/** One edition, then one or more qualities its stream offers, each with its size. */
function DownloadForm(props: { readonly movie: MovieDetail; readonly onDone: () => void }) {
  const editions = liveEditions(props.movie).filter((variant) => variant.episodes.length > 0);
  const [variantId, setVariantId] = useState<number | undefined>(() => (editions.find((variant) => variant.variantId === props.movie.primaryVariantId) ?? editions[0])?.variantId);
  const renditions = useRenditions(props.movie.code, variantId);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const start = useStartDownload();
  const variant = editions.find((candidate) => candidate.variantId === variantId);
  const options = [...(renditions.data?.renditions ?? [])].sort((left, right) => (right.bandwidth ?? 0) - (left.bandwidth ?? 0));
  const total = options.filter((option) => chosen.has(option.resolution)).reduce((sum, option) => sum + (option.estimatedBytes ?? 0), 0);

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (variantId !== undefined && chosen.size > 0) {
          start.mutate({ code: props.movie.code, variantId, resolutions: [...chosen] }, { onSuccess: props.onDone });
        }
      }}
    >
      <DialogHeader>
        <DialogTitle>Download {props.movie.code}</DialogTitle>
        <DialogDescription>zyx keeps a copy on its disk: the player uses it, even after the source drops the video, and you can save it as an MP4 for another device.</DialogDescription>
      </DialogHeader>
      {editions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No edition of this movie has a video to download.</p>
      ) : (
        <>
          {editions.length > 1 ? (
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Edition">
              {editions.map((candidate) => (
                <Button
                  key={candidate.variantId}
                  type="button"
                  size="sm"
                  variant={candidate.variantId === variantId ? 'secondary' : 'outline'}
                  aria-pressed={candidate.variantId === variantId}
                  onClick={() => {
                    setVariantId(candidate.variantId);
                    setChosen(new Set());
                  }}
                >
                  {editionLabel(candidate.edition)}
                </Button>
              ))}
            </div>
          ) : null}
          {variant !== undefined && variant.episodes.length > 1 ? <p className="text-sm text-muted-foreground">All {variant.episodes.length} parts are downloaded.</p> : null}
          {renditions.isPending ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Asking the stream which qualities it has…
            </p>
          ) : renditions.isError ? (
            <ErrorState error={renditions.error} onRetry={() => void renditions.refetch()} />
          ) : (
            <ul className="space-y-2">
              {options.map((option) => (
                <li key={option.resolution} className="flex items-center gap-2">
                  <Checkbox
                    id={`quality-${option.resolution}`}
                    checked={chosen.has(option.resolution)}
                    onCheckedChange={(checked) => {
                      const next = new Set(chosen);
                      if (checked === true) {
                        next.add(option.resolution);
                      } else {
                        next.delete(option.resolution);
                      }
                      setChosen(next);
                    }}
                  />
                  <Label htmlFor={`quality-${option.resolution}`} className="font-normal">
                    {qualityLabel(option.resolution)}
                    <span className="text-muted-foreground">
                      {option.resolution === 'source' ? '' : ` · ${option.resolution}`}
                      {option.estimatedBytes === undefined ? '' : ` · up to ${formatBytes(option.estimatedBytes)}`}
                    </span>
                  </Label>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <DialogFooter className="items-center gap-3">
        {total > 0 ? <span className="text-sm text-muted-foreground">Up to {formatBytes(total)} in all</span> : null}
        <Button type="submit" disabled={chosen.size === 0 || start.isPending}>
          <Download />
          Download
        </Button>
      </DialogFooter>
    </form>
  );
}

/** The Refresh and Download buttons of a movie page, each with its dialog. */
export function MovieActions(props: { readonly movie: MovieDetail }) {
  const [open, setOpen] = useState<'refresh' | 'download' | undefined>(undefined);
  const close = () => setOpen(undefined);
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" onClick={() => setOpen('refresh')}>
        <RefreshCw />
        Refresh
      </Button>
      <Button variant="outline" onClick={() => setOpen('download')}>
        <Download />
        Download
      </Button>
      <Dialog open={open !== undefined} onOpenChange={(next) => (next ? undefined : close())}>
        <DialogContent className="max-w-lg">
          {open === 'refresh' ? <RefreshForm movie={props.movie} onDone={close} /> : open === 'download' ? <DownloadForm movie={props.movie} onDone={close} /> : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
